import React, {useState, useEffect, useCallback} from 'react';
import {
  View,
  Platform,
  ScrollView,
  Dimensions,
  StyleSheet,
  Image,
  ActivityIndicator,
} from 'react-native';
import {TouchableWithoutFeedback} from 'react-native-gesture-handler';

import {observer} from 'mobx-react';
import {useToast} from 'react-native-fast-toast';

import {makeStyles, Overlay, useTheme} from 'react-native-elements'; // rne-netzon replaced
import {colorPalette} from '../../../core/config/color.config';

import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {Dropdown} from 'react-native-element-dropdown';

import {
  IconName,
  RNEText,
  SVGIcon,
  RNEButton,
  RNSearchableDropdown,
  RNSearchableDropdownOption,
  RNEInput,
} from '../../components';
import useStores from '../../stores/useStores';

import {MedicalHistoryCard, MedicalHistoryModal} from './../../views';
import {MedicalHistoryEnum} from '../../views/medical-history/data/medicalHistory';
import {NavigationKey} from '../../navigations/config';
import {Text} from 'react-native';
import {width, totalSize, height} from 'react-native-dimension';
import LightAppLogo from '../../../assets/logo/logo-light.png';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

const medicalHistoryList: {
  label: string;
  icon: IconName;
  key: MedicalHistoryEnum;
  navigation: NavigationKey;
}[] = [
  {
    label: 'Medical Condition',
    icon: 'hc-icon-medical-condition',
    key: MedicalHistoryEnum.CONDITION,
    navigation: NavigationKey.CONDITION,
  },
  {
    label: 'Family Condition',
    icon: 'hc-icon-family-condition',
    key: MedicalHistoryEnum.FAMILY_MEMBER_HISTORY,
    navigation: NavigationKey.FAMILY_MEMBER_HISTORY,
  },
  {
    label: 'Maintenance',
    icon: 'hc-icon-maintenance',
    key: MedicalHistoryEnum.MEDICATION_STATEMENT,
    navigation: NavigationKey.MEDICATION_STATEMENT,
  },
  {
    label: 'Hospitalization',
    icon: 'hc-icon-hospitalization',
    key: MedicalHistoryEnum.PROCEDURE,
    navigation: NavigationKey.PROCEDURE,
  },
  {
    label: 'Personal and Social',
    icon: 'hc-icon-personal-and-social',
    key: MedicalHistoryEnum.PERSONAL_AND_SOCIAL,
    navigation: NavigationKey.PERSONAL_AND_SOCIAL,
  },
  {
    label: 'Allergies',
    icon: 'hc-icon-allergies',
    key: MedicalHistoryEnum.ALLERGY_INTOLERANCE,
    navigation: NavigationKey.ALLERGY_INTOLERANCE,
  },
];

const PatientLandingScreen = () => {
  const navigation = useNavigation();
  const {theme} = useTheme();
  const {patientProfileStore, specializationStore} = useStores();
  const toast = useToast();

  const [scrollViewHeight, setScrollViewHeight] = useState(0);

  const activeProfile = patientProfileStore.activeProfileData;
  const {setCanRefresh} = usePullDownRefreshContext();

  const [searchKey, setSearchKey] = useState('');
  const [searchByDoctor, setSearchByDoctor] = useState(false);

  const [dropdownValue, setDropdownValue] = useState<
    number | string | null | undefined
  >();
  const [dropdownOptions, setDropdownOptions] = useState<
    RNSearchableDropdownOption[]
  >([]);

  const [selectedMedicalHistory, setSelectedMedicalHistory] =
    useState<MedicalHistoryEnum>(MedicalHistoryEnum.ALLERGY_INTOLERANCE);

  const [showDialog, setShowDialog] = useState(false);

  const switchSearchBy = () => {
    setSearchByDoctor(!searchByDoctor);
  };

  const onDropdownChange = (val: string | number | null | undefined) => {
    // console.log(val, 'dsa');
    if (dropdownValue !== val) {
      setDropdownValue(val);
      goSearchDoctor(searchKey, val);
    }
  };

  const toggleModal = (medicalHistoryKey: MedicalHistoryEnum) => {
    setShowDialog(true);
    setSelectedMedicalHistory(medicalHistoryKey);
  };

  const goSearchDoctor = (
    inputValue: string,
    dpValue: number | string | null | undefined,
  ) => {
    const inputVal = searchByDoctor ? inputValue : '';
    const dpVal = !searchByDoctor ? dpValue : '';

    navigation.navigate(NavigationKey.PATIENT_SEARCH_RESULT, {
      searchKey: inputVal,
      searchDropValue: dpVal,
      searchType: searchByDoctor ? 'name' : 'specialization',
    });

    setSearchKey('');
    setTimeout(() => {
      setDropdownValue(undefined);
    }, 10);
  };

  const onSuccessHistoryUpdate = () => {
    toast?.show('Successfully updated the medical data');
    setShowDialog(false);
  };

  useEffect(() => {
    setDropdownOptions(
      specializationStore.specializations.map(specialization => ({
        name: specialization.value,
        value: specialization.specializationId,
      })),
    );
  }, [specializationStore.specializations]);

  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  if (!activeProfile) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white', // matches app theme
        }}>
        <ActivityIndicator size="large" color={colorPalette.primary} />
      </View>
    );
  }

  return (
    <>
      <Overlay
        isVisible={showDialog}
        overlayStyle={sharedStyles.overlayContainer}>
        <MedicalHistoryModal
          medicalHistoryType={selectedMedicalHistory}
          onCancel={() => setShowDialog(false)}
          onSuccess={() => onSuccessHistoryUpdate()}
        />
      </Overlay>
      {Platform.OS === 'web' ? (
        <ScrollView>
          <View style={webStyles.mainContainer}>
            <View>
              <RNEText
                label={`Hi, ${activeProfile?.firstName}!`}
                variant="header"
                weight="bold"
                color="accent"
              />
              <RNEText
                label="How do you feel today?"
                variant="body"
                color="black"
              />
            </View>
            <View style={webStyles.searchContainer}>
              <View style={webStyles.dropdownContainer}>
                {searchByDoctor ? (
                  <View style={webStyles.searchInput}>
                    <RNEInput
                      value={searchKey}
                      onChangeText={setSearchKey}
                      placeholder="Input Doctor's Name here"
                      variant="shadowed"
                      inputContainerStyle={inputContainerStyle}
                      height={48}
                      onSubmitEditing={() =>
                        goSearchDoctor(searchKey, dropdownValue)
                      }
                    />
                  </View>
                ) : (
                  <View style={webStyles.dropdown}>
                    <RNSearchableDropdown
                      value={dropdownValue}
                      placeholder="Input Doctor's Specialization here"
                      options={dropdownOptions}
                      onChange={onDropdownChange}
                    />
                  </View>
                )}

                <View style={webStyles.buttonContainer}>
                  <RNEButton
                    title="Find a Doctor"
                    color="secondary"
                    style={webStyles.buttonStyle}
                    onPress={() => goSearchDoctor(searchKey, dropdownValue)}
                  />
                </View>
              </View>

              <View style={webStyles.textButton}>
                <TouchableWithoutFeedback
                  onPress={switchSearchBy}
                  accessibilityRole="button">
                  <RNEText
                    label={
                      searchByDoctor
                        ? "Input Doctor's Specialization instead"
                        : 'Find a Doctor by Name Instead'
                    }
                    variant="button"
                    weight="bold"
                    color="secondary"
                  />
                </TouchableWithoutFeedback>
              </View>
            </View>
            <View style={[webStyles.cardsContainer]}>
              {medicalHistoryList.map((item, index) => (
                <View key={index} style={webStyles.cardContainer}>
                  <MedicalHistoryCard
                    width={328}
                    height={120}
                    label={item.label}
                    icon={<SVGIcon name={item.icon} size={80} />}
                    onPress={() => toggleModal(item.key)}
                  />
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      ) : (
        <ScrollView
          onScroll={handleScroll}
          onLayout={e => {
            setScrollViewHeight(e.nativeEvent.layout.height);
          }}
          onContentSizeChange={(contentWidth, contentHeight) => {
            setContentHeight(contentHeight);
          }}
          keyboardShouldPersistTaps="handled">
          <View style={mobileStyles.mainContainer}>
            <View style={mobileStyles.greetingContainer}>
              <RNEText
                label={`Hi, ${activeProfile?.firstName}!`}
                variant="header"
                weight="bold"
                color="accent"
              />
              <RNEText
                label="How do you feel today?"
                variant="body"
                color="black"
              />
            </View>
            <>
              {searchByDoctor ? (
                <View style={mobileStyles.searchInput}>
                  <RNEInput
                    value={searchKey}
                    onChangeText={setSearchKey}
                    placeholder="Input Doctor's Name here"
                    inputContainerStyle={inputContainerStyle}
                    onSubmitEditing={() =>
                      goSearchDoctor(searchKey, dropdownValue)
                    }
                    style={{
                      color: theme.colors?.grey0,
                      fontSize: totalSize(1.42),
                      fontFamily: 'Quicksand-Medium',
                    }}
                    returnKeyType="search"
                  />
                </View>
              ) : (
                <View style={mobileStyles.dropdownContainer}>
                  <Dropdown
                    data={dropdownOptions}
                    placeholderStyle={{
                      color: theme.colors?.grey0,
                      fontSize: totalSize(1.42),
                      paddingLeft: width(2.6),
                      fontFamily: 'Quicksand-Medium',
                    }}
                    style={{
                      borderWidth: 1,
                      borderColor: theme.colors?.greyOutline,
                      borderRadius: totalSize(4),
                      padding: height(1.4),
                      paddingBottom:
                        Platform.OS === 'android' ? height(1.6) : height(1.4),
                      paddingLeft:
                        Platform.OS === 'android' ? height(1.5) : height(1.4),

                      shadowOffset: {
                        width: -5,
                        height: 10,
                      },
                      shadowOpacity: 0.5,
                      shadowRadius: 20,
                      shadowColor: colorPalette.grey,
                      backgroundColor: colorPalette.white,
                      zIndex: 50,
                      elevation: 50,
                    }}
                    mode={'default'}
                    maxHeight={300}
                    labelField="name"
                    valueField="value"
                    placeholder="Input Doctor's Specialization here"
                    // searchPlaceholder="Search..."
                    value={dropdownValue}
                    onChange={val => onDropdownChange(val.value)}
                  />
                  {/* <RNSearchableDropdown
                    placeholder="Input Doctor's Specialization here"
                    value={dropdownValue}
                    options={dropdownOptions}
                    title={'Select specialization'}
                    onChange={onDropdownChange}
                  /> */}
                </View>
              )}

              <TouchableWithoutFeedback onPress={switchSearchBy}>
                <View
                  accessibilityRole="button"
                  style={mobileStyles.textButton}>
                  <RNEText
                    label={
                      searchByDoctor
                        ? "Input Doctor's Specialization instead"
                        : 'Find a Doctor by Name Instead'
                    }
                    variant="body"
                    weight="bold"
                    color="secondary"
                  />
                </View>
              </TouchableWithoutFeedback>
            </>

            <View style={mobileStyles.cardsContainer}>
              {medicalHistoryList.map((item, index) => (
                <MedicalHistoryCard
                  key={index}
                  height={100}
                  label={item.label}
                  icon={<SVGIcon name={item.icon} size={48} />}
                  onPress={() =>
                    navigation.navigate(
                      NavigationKey.PATIENT_MEDICAL_HISTORY_STACK,
                      {screen: item.navigation},
                    )
                  }
                />
              ))}
            </View>
          </View>
        </ScrollView>
      )}
    </>
  );
};

export default observer(PatientLandingScreen);

const inputContainerStyle = {
  height: 48,
  borderRadius: 30,
  shadowOffset: {
    width: -5,
    height: 10,
  },
  shadowOpacity: 0.5,
  shadowRadius: 20,
  shadowColor: colorPalette.grey,
  backgroundColor: colorPalette.white,
  zIndex: 50,
  elevation: 50,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    padding: 100,
  },
  searchContainer: {
    marginTop: 32,
    zIndex: 20,
  },
  dropdownContainer: {
    flexDirection: 'row',
    zIndex: 20,
  },
  dropdown: {
    height: 48,
    flex: 1,
    justifyContent: 'center',
  },
  searchInput: {
    height: 48,
    flex: 1,
    justifyContent: 'center',
  },
  buttonContainer: {
    marginLeft: 24,
  },
  buttonStyle: {
    width: 168,
  },
  textButton: {
    marginTop: 24,
    zIndex: 0,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  cardsContainer: {
    marginLeft: -20,
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cardContainer: {
    marginRight: 48,
    marginBottom: 0,
    flexShrink: 1,
    flexGrow: 0,
    minWidth: 250,
    maxWidth: 332,
    width: '100%',
  },
  titleContainer: {
    flexShrink: 1,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    paddingVertical: totalSize(2.5),
    paddingHorizontal: 40,
  },
  greetingContainer: {},
  searchInput: {
    marginTop: height(2.5),
    marginBottom: height(2),
    height: height(5.2),
  },
  dropdownContainer: {
    marginTop: height(2.5),
    // marginBottom: height(2),
    marginBottom: Platform.OS === 'android' ? height(1.7) : height(2),
  },
  textButton: {
    alignItems: 'flex-end',
    height: 60,
    zIndex: 0,
    elevation: 0,
  },
  cardsContainer: {
    zIndex: 10,
    elevation: 10,
    marginTop: 0,
    marginBottom: 12,
    marginLeft: -20,
    marginRight: 20,
  },
  iconContainer: {
    paddingRight: 28,
  },
});

const sharedStyles = StyleSheet.create({
  root: {
    height: '100%',
    flexDirection: 'row',
    width: '100%',
  },
  iconContainer: {
    paddingRight: 28,
  },
  overlayContainer: {
    borderRadius: 20,
  },
});
